<template>
  <div class="modal-card" style="width: 960px">
    <header class="modal-card-head">
      <p class="modal-card-title"><translate>tableSelect.header.{{ field }}</translate></p>
    </header>
    <section class="modal-card-body">
      <b-field :label="$gettext('tableSelect.queryString')">
        <b-input ref="input" :icon="icon" v-model="tableQuery" @input="queryForTable" value="" placeholder="Start typing..."></b-input>
      </b-field>
      <b-table narrowed :paginated="true" :per-page="5" :loading="isFetching" :data="filteredTableContent">
        <template slot-scope="props">
          <b-table-column label="" width="40">
            <a class="is-primary" @click="selectRow(props.row._id)"><b-icon size="is-small" icon="plus"/></a>
          </b-table-column>
          <b-table-column
              v-for="(col, i) in tableColumns"
              :key="i"
              :label="$gettext(`${entityName}.form.tableSelect.${col.label}`)"
              :width="col.width"
          >
            <template v-if="col.field === '_stringRep'">
              <fob-entity-decorator :resourceData="props.row">
                {{ props.row[col.field] }}
              </fob-entity-decorator>
            </template>
            <template v-else>
              {{ props.row[col.field] }}
            </template>
          </b-table-column>
        </template>
        <template slot="empty">
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p>
                <b-icon
                    icon="exclamation"
                    size="is-large">
                </b-icon>
              </p>
              <template v-if=isFetching>
                <translate>tableSelect.emptyTablePlaceholder.isFetching</translate>
              </template>
              <template v-else-if="tableQuery.length === 0">
                <translate>tableSelect.emptyTablePlaceholder.noInput</translate>
              </template>
              <template v-else-if="tableQuery.length > 0 && tableQuery.length < method.minLength">
                <translate>tableSelect.emptyTablePlaceholder.minLength</translate>
              </template>
              <template v-else-if="tableQuery.length >= method.minLength">
                <p>
                  <translate>tableSelect.emptyTablePlaceholder.noResults</translate>
                </p>
              </template>
            </div>
          </section>
        </template>
      </b-table>

      <h3 class="is-size-6" style="margin-top: 20px"><strong><translate>tableSelect.selected</translate></strong></h3>

      <div style="height: 300px; overflow-y: scroll; overflow-x: hidden">

        <table style="width: 100%" class="table is-narrow has-mobile-cards">
          <thead>
            <tr class="is-unselectable">
              <th style="width: 40px">
                <!-- for the delete button -->
              </th>
              <th v-for="(col, i) in tableColumns"
                  :key="i"
                  :style="{ width: col.width + 'px' }"
              ><translate>{{ `${entityName}.form.tableSelect.${col.label}` }}</translate></th>
            </tr>
          </thead>
          <draggable v-model="selectedContent" @change="$emit('input', selectedContent)"
            :element="'tbody'"
            :options="{
              draggable: 'tr',
              filter: '.delete-icon'
            }"
          >
            <tr v-for="(line, i) in selectedContent" :key="i">
              <td class="delete-icon" style="width: 40px;">
                <a class="is-primary" @click="dropRow(line._id)"><b-icon type="is-carnelian" size="is-small" icon="minus"/></a>
              </td>

              <td v-for="(col, i) in tableColumns"
                  style="cursor: move"
                  :key="i"
                  >
                  <template v-if="col.field === '_stringRep'">
                    <fob-entity-decorator :resourceData="line">
                      {{ line[col.field] }}
                    </fob-entity-decorator>
                  </template>
                  <template v-else>
                    {{ line[col.field] }}
                  </template>
              </td>
            </tr>
          </draggable>
        </table>
      </div>
    </section>

    <footer class="modal-card-foot">
      <button class="button is-primary" type="button" @click="$parent.close()">
        <translate>tableSelect.button.close</translate>
      </button>
      <button
        class="button" type="button" @click="prepareCreation"
        v-if="allowCreate"
      >
        <translate>tableSelect.button.create</translate>
      </button>
    </footer>

    <fob-entity-create-modal
      :active.sync="showCreateModal"
      :schema="schema"
      :field="field"
      @submit="submitCreated"
      v-model="created">
    </fob-entity-create-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import api from '@/api'
import entityValue from '@/components/EntityValue'
import entityCreateModal from '@/components/form/EntityCreateModal'
import entityDecorator from '@/components/EntityDecorator'

export default {
  name: 'fob-entity-table-select',
  data () {
    return {
      created: null,
      showCreateModal: false,
      tableQuery: '',
      isFetching: false,
      tableContent: [],
      selectedContent: this.value || []
    }
  },
  props: {
    route: String,
    field: String,
    method: Object,
    schema: Object,
    allowCreate: {
      type: Boolean,
      default: true
    },
    icon: {
      type: String,
      default: ''
    },
    value: {
      type: Array,
      default () {
        return []
      }
    }
  },
  components: {
    draggable,
    'fob-entity-value': entityValue,
    'fob-entity-create-modal': entityCreateModal,
    'fob-entity-decorator': entityDecorator
  },
  methods: {
    prepareCreation () {
      let Constructor = this.schema.class
      this.created = new Constructor({
        _id: `__${Date.now()}`
      })
      this.showCreateModal = true
    },
    submitCreated () {
      let Constructor = this.schema.class
      let newPerson = new Constructor(this.created)
      newPerson._stringRep = this.schema.preliminaryStringRep(newPerson)
      this.selectedContent.push(newPerson)
      this.$emit('input', this.selectedContent)
    },
    dropRow (theid) {
      this.selectedContent = this.selectedContent.filter((item) => item._id !== theid)
      this.$emit('input', this.selectedContent)
    },
    selectRow (theid) {
      const result = this.tableContent.find((data) => data._id === theid)
      if (!this.selectedContent.includes(result)) {
        this.selectedContent.push(result)
        this.$emit('input', this.selectedContent)
      }
      // if filteredTableContent length is 1,
      // then reset input (correct empty table message)
      if (this.filteredTableContent.length === 0) {
        this.tableQuery = ''
      }
    },
    queryForTable () {
      if (this.tableQuery.length >= this.method.minLength) {
        this.isFetching = true
        let param
        if (this.method.multiValue) {
          param = this.tableQuery.split(/\s+/).filter(x => x !== '')
        } else {
          param = this.tableQuery
        }

        let opts = this.method.opts || {}

        this.autocomplete({ rep: param, ...opts })
            .then(result => {
              // optional chaining `result?.data` not supported
              let data = result ? result.data : null

              if (this.data && this.data.length === 0) {
                this.tableContent = []
              } else if (data) {
                this.tableContent = data
              } else {
                this.tableContent = []
              }
              this.isFetching = false
            }, response => {
              this.isFetching = false
            })
      }
    }
  },
  mounted () {
    this.$refs.input.focus()
    // if minlength is set to zero on start,
    // trigger fetching available data and show it on table
    this.queryForTable()
  },
  computed: {
    tableColumns () {
      if (this.schema) {
        if (this.schema.tableSelectColumns) {
          return this.schema.tableSelectColumns
        }
        return this.schema.fields.filter(x => x.table === true)
      } else {
        return [
          {
            field: '_id',
            label: 'ID',
            width: '50'
          },
          {
            field: 'vorname',
            label: 'Vorname',
            width: '200'
          },
          {
            field: 'name',
            label: 'Nachame',
            width: '200'
          }
        ]
      }
    },
    entityName () {
      return this.schema.className[0].toUpperCase() + this.schema.className.slice(1)
    },
    autocomplete () {
      return api.methods.autocomplete(this.route, this.method.name)
    },
    filteredTableContent () {
      return this.tableContent.filter((item) =>
        !this.selectedContent.some((selected) =>
          item._id === selected._id))
    },
    filteredDataArray () {
      return this.arrayExample.data.filter((option) => {
        return option
          .toString()
          .toLowerCase()
          .indexOf(this.arrayExample.name.toLowerCase()) >= 0
      })
    }
  }
}
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/form/EntityTableSelect.vue